import * as _ from 'lodash';
import * as moment from 'moment';
import * as qs from 'query-string';
import { connect, Dispatch } from 'react-redux';
import { MutableRefObject, useContext, useEffect, useReducer, useState } from 'react';
import { IDonorDeclines, IFwtSummary, IRecipient } from '@nkr/models/index';
import { IApplicationState, AppActionCreators, RecipientActionCreators, IRecipientDonorDeclinesResponse } from '@nkr/store/index';
import { RecipientProfileContext } from '../models';
import { donorDeclinesReducer, initialState } from './state';
import { CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { Panel } from 'react-bootstrap';
import { ProfileWrapper } from '../styles';
import { DeclineRatio } from './decline-ratio-type';
import DonorDeclinesGrid from './donor-declines-grid';
import Filter from './filter';
import MortalityBreakdown from '@nkr/components/mortality-breakdown-modal';
import FwlDetailsModal from '@nkr/views/target-new/modals/fwt-details-modal';

const Risk = {
    NoData: -1,
    Zero: 0,
    Low: 10,
    Intermediate: 20,
    High: 30
};

interface IProps {
    saveRef: MutableRefObject<any>
}

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getDonorDeclines: (centerId: number, recipientId: number) => Promise<IRecipientDonorDeclinesResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const DonorDeclines = (props: TProps) => {
    const context = useContext(RecipientProfileContext);
    const [state, dispatch] = useReducer(donorDeclinesReducer, initialState);
    const [gridApi, setGridApi] = useState<GridApi | null>(null);

    useEffect(() => {
        getData();
    }, []);

    const getData = () => {
        let parsed = qs.parse(location.search.length > 0 ? location.search : encodeURI(''), { arrayFormat: 'bracket' });
        let ddrtype = parsed.ddrtype != null ? parseInt(parsed.ddrtype) : DeclineRatio.Current;
        let showCalc = parsed.showCalc != null ? parseInt(parsed.showCalc) : -1;

        props.showLoading("Getting donor declines, one moment please....").then(() => {
            props.getDonorDeclines(context.centerId, context.recipientId)
                .then(dataResponse => {
                    if (dataResponse.data.success) {
                        let recipient = dataResponse.data.recipientData.recipient;

                        dispatch({
                            type: 'afterLoad',
                            payload: {
                                donorDeclineData: dataResponse.data.recipientData.potentialDonors,
                                recipient,
                                declineRatioAllTime: dataResponse.data.recipientData.declineRatioAllTime,
                            }
                            // sortedDonorDeclinesData: list
                        });

                        dispatch({
                            type: 'changeDeclineRatioType',
                            payload: ddrtype,
                            // sortedDonorDeclinesData: list
                        });

                        if (showCalc == 1) {
                            dispatch({
                                type: 'updateMortalityRiskCal',
                                payload: {
                                    show: true,
                                    recipientAlias: recipient.alias,
                                    data: recipient.mortalityCalculations
                                }
                            });
                        }

                        if (showCalc == 2) {
                            let hardToMatch = recipient?.isHardToMatch;
                            let isVoucherHolder = recipient?.isVoucherHolder;
                            let isPaired = recipient?.isPairedRecipient;
                            let age = Number(recipient?.age ?? '0');

                            let fwtSummary: IFwtSummary = {
                                recipientId: recipient.id,
                                recipientAlias: recipient.alias,
                                hardToMatch,
                                isVoucherHolder,
                                isPairedRecipient: isPaired,
                                patientCpra: recipient?.cpra4 ?? 0,
                                patientHistoricalDeclineRatio: recipient?.declineRatioHistoric ?? 0,
                                donorsNeeded: recipient?.donorsNeeded ?? 0,
                                aboCombo: recipient?.aboCombo ?? '',
                                donorsCompatibleABO: recipient?.donorCompatibleAbo.toString() ?? '',
                                aboAndAgeCompatibleDonors: recipient?.aboAndAgeCompatibleDonors.toString() ?? '',
                                recipientBloodType: recipient?.bloodType ?? '',
                                categories: recipient?.voucherCategories ?? [],
                                fwt: recipient?.forecastedWaitTime.toString() ?? '',
                                ewt: recipient?.estimatedWaitTimeRaw ?? '',
                                awt: recipient?.monthsWaiting.toString() ?? '',
                                age: age
                            };

                            dispatch({
                                type: 'updateFWTCal',
                                payload: {
                                    show: true,
                                    fwtSummary
                                }
                            });
                        }
                    }
                })
                .catch(() => {
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const getFilteredList = (list: IDonorDeclines[], type: DeclineRatio) => {
        let startDate = moment(state.startDate);
        let endDate = moment(state.endDate).add(23, 'hours').add(59, 'minutes');

        let filteredList = list.filter(x => x.isPotentialDonor);

        if (type == DeclineRatio.Historic) {
            filteredList = list.filter(x => x.isHistoric);
        }

        if (type == DeclineRatio.AllTime) {
            filteredList = list.filter(x => x.isPotentialAllTimeDonor);
        }

        return _.orderBy(filteredList, x => moment(x.decisionDateTime), ['desc']);
    };

    const handleOnGridSwitch = (declineRatioType: DeclineRatio) => {
        dispatch({
            type: 'changeDeclineRatioType',
            payload: declineRatioType,
        })

        setUrlParameters(declineRatioType);
    }

    const handleOnStartDateChange = (startDate: string) => {
        dispatch({
            type: 'changeStartDate',
            payload: startDate
        })
    }

    const handleOnEndDateChange = (endDate: string) => {
        dispatch({
            type: 'changeEndDate',
            payload: endDate
        })
    }

    const handleShowMortalityBreakdown = (recipient: IRecipient) => {
        dispatch({
            type: 'updateMortalityRiskCal',
            payload: {
                show: true,
                recipientAlias: recipient.alias,
                data: recipient.mortalityCalculations
            }
        });
    }

    const handleMortalityBreakdownClose = () => {
        dispatch({
            type: 'updateMortalityRiskCal',
            payload: {
                show: false,
                recipientAlias: '',
                data: null
            }
        });
    }

    const handleShowFwt = (recipient: IRecipient) => {
        let hardToMatch = recipient?.isHardToMatch;
        let isVoucherHolder = recipient?.isVoucherHolder;
        let isPaired = recipient?.isPairedRecipient;
        let age = Number(recipient?.age ?? '0');

        let fwtSummary: IFwtSummary = {
            recipientId: recipient.id,
            recipientAlias: recipient.alias,
            hardToMatch,
            isVoucherHolder,
            isPairedRecipient: isPaired,
            patientCpra: recipient?.cpra4 ?? 0,
            patientHistoricalDeclineRatio: recipient?.declineRatioHistoric ?? 0,
            donorsNeeded: recipient?.donorsNeeded ?? 0,
            aboCombo: recipient?.aboCombo ?? '',
            donorsCompatibleABO: recipient?.donorCompatibleAbo.toString() ?? '',
            aboAndAgeCompatibleDonors: recipient?.aboAndAgeCompatibleDonors.toString() ?? '',
            recipientBloodType: recipient?.bloodType ?? '',
            categories: recipient?.voucherCategories ?? [],
            fwt: recipient?.forecastedWaitTime.toString() ?? '',
            ewt: recipient?.estimatedWaitTimeRaw ?? '',
            awt: recipient?.monthsWaiting.toString() ?? '',
            age: age
        };

        dispatch({
            type: 'updateFWTCal',
            payload: {
                show: true,
                fwtSummary
            }
        });
    };

    const handleFwtClose = () => {
        dispatch({
            type: 'updateFWTCal',
            payload: {
                show: false,
                fwtSummary: null
            }
        });
    };

    const onGridReady = (event: GridReadyEvent) => setGridApi(event.api);

    const handleExport = () => {
        let { declineRatioCurrent: ddrc, declineRatioHistoric: ddrh, alias } = state.recipient
        let type = 'current';

        if (state.declineRatioType == DeclineRatio.Historic) {
            type = 'historic';
        }

        if (state.declineRatioType == DeclineRatio.AllTime) {
            type = 'alltime';
        }

        let params: CsvExportParams = {
            columnGroups: true,
            fileName: `${alias}-donor-decline-ratio-${type}.csv`,
            processCellCallback: (params: ProcessCellForExportParams) => {
                if (params.column.getColId() == 'emm') {
                    let singleEpletMmRisk = params.node.data.singleEpletMmRisk;
                    let { singleEpletRisk, overallRisk, isAboCompatible } = singleEpletMmRisk;

                    let data = 'ABOi'
                    if (singleEpletRisk == Risk.Zero) {
                        data = '0';
                    } else if (singleEpletRisk == Risk.Low) {
                        data = 'Low';
                    } else if (singleEpletRisk == Risk.Intermediate) {
                        data = 'Medium';
                    } else if (singleEpletRisk == Risk.High) {
                        data = 'High';
                    }

                    if (overallRisk != null && isAboCompatible == false) {
                        data = 'ABOi';
                    }

                    return data;
                }

                return params.value;
            }
        };

        gridApi.exportDataAsCsv(params);
    }

    const setUrlParameters = (declineRatioType: DeclineRatio) => {
        let paramList = [];

        paramList.push(encodeURI('ddrtype=' + declineRatioType));

        let params = paramList.join('&');
        let newUrl = '/center/' + context.centerId + '/recipient/' + context.recipientId + '/profile/donor-declines?' + params;

        window.history.pushState({}, '', newUrl);
    }

    if (context.headerInfo == null || state.recipient == null)
        return <></>;

    var donorList = getFilteredList(state.donorDeclineData, state.declineRatioType);

    return (
        <>
            <ProfileWrapper style={{ padding: 5 }}>
                <Filter
                    recipient={state.recipient}
                    declineRatioAllTime={state.declineRatioAllTime}
                    startDate={state.startDate}
                    endDate={state.endDate}
                    declineRatioType={state.declineRatioType}
                    declineRatioAdjusted={state.declineRatioAdjusted}
                    onSwitchDeclineRatioGrid={handleOnGridSwitch}
                    onStartDateChange={handleOnStartDateChange}
                    onEndDateChange={handleOnEndDateChange}
                    onExportCSV={handleExport}
                    onShowMortalityBreakdown={handleShowMortalityBreakdown}
                    onShowFWTBreakdown={handleShowFwt}
                />

                <DonorDeclinesGrid
                    data={donorList}
                    centerId={context.centerId}
                    onGridReady={onGridReady}
                />
            </ProfileWrapper>

            <MortalityBreakdown
                show={state.mortalityBreakdown.show}
                recipientAlias={state.mortalityBreakdown.recipientAlias}
                data={state.mortalityBreakdown.data}
                onClose={handleMortalityBreakdownClose}
            />

            <FwlDetailsModal
                show={state.fwt.show}
                summaryData={state.fwt.fwtSummary}
                onClose={handleFwtClose}
            />
        </>
    );
}

const mapStateToProps = (appState: IApplicationState): IStateProps => {
    return {};
};

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getDonorDeclines: (centerId, recipientId) => {
            return dispatch(RecipientActionCreators.getDonorDeclines(centerId, recipientId));
        }
    };
};

export default connect<IStateProps, {}, IProps>(
    mapStateToProps,
    mapDispatchToProps)(DonorDeclines);
